import { PIMS_SYSTEMS, type PimsSystem, type PromptTemplateAudience } from '@astro/shared-ui';

export type AudienceMode = 'everyone' | 'include' | 'exclude';

export interface AudienceFormValues {
  audienceMode: AudienceMode;
  audienceSystems: PimsSystem[];
}

export const AUDIENCE_MODE_LABELS: Record<AudienceMode, string> = {
  everyone: 'Everyone',
  include: 'Only these PIMS',
  exclude: 'All except these PIMS',
};

const PIMS_SYSTEM_ORDER = Object.keys(PIMS_SYSTEMS) as PimsSystem[];

const inDisplayOrder = (systems: readonly string[]): PimsSystem[] =>
  PIMS_SYSTEM_ORDER.filter((system) => systems.includes(system));

export const formatAudienceLabel = (
  audience: PromptTemplateAudience | undefined,
  hasInvalidAudience: boolean
): string => {
  if (hasInvalidAudience) {
    return 'Invalid rule';
  }
  const pimsRule = audience?.pims;
  if (!pimsRule) {
    return 'Everyone';
  }
  const names = inDisplayOrder(pimsRule.systems)
    .map((system) => PIMS_SYSTEMS[system])
    .join(', ');
  return pimsRule.mode === 'include' ? `Only: ${names}` : `All except: ${names}`;
};

export const audienceToFormValues = (
  audience: PromptTemplateAudience | undefined
): AudienceFormValues => {
  const pimsRule = audience?.pims;
  if (!pimsRule) {
    return { audienceMode: 'everyone', audienceSystems: [] };
  }
  return { audienceMode: pimsRule.mode, audienceSystems: inDisplayOrder(pimsRule.systems) };
};

export const formValuesToAudience = ({
  audienceMode,
  audienceSystems,
}: AudienceFormValues): PromptTemplateAudience => {
  if (audienceMode === 'everyone') {
    return null;
  }
  return { pims: { mode: audienceMode, systems: inDisplayOrder(audienceSystems) } };
};

export const isAudienceMode = (value: string): value is AudienceMode =>
  Object.prototype.hasOwnProperty.call(AUDIENCE_MODE_LABELS, value);

export const toPimsSystems = (values: readonly string[]): PimsSystem[] => inDisplayOrder(values);

export const PIMS_SYSTEM_OPTIONS = PIMS_SYSTEM_ORDER.map((system) => ({
  value: system,
  label: PIMS_SYSTEMS[system],
}));
